import _slicedToArray from "C:\\Users\\Kristaps\\projects\\proj261\\site\\node_modules\\@babel\\runtime\\helpers\\esm\\slicedToArray.js";
import _toConsumableArray from "C:\\Users\\Kristaps\\projects\\proj261\\site\\node_modules\\@babel\\runtime\\helpers\\esm\\toConsumableArray.js";
var _excluded = ["multiple", "data", "value", "onChange", "accessors", "className", "messages", "disabled", "renderItem", "renderGroup", "searchTerm", "groupBy", "elementRef", "optionComponent", "renderList"];

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}
/* eslint-disable @typescript-eslint/no-empty-function */


import cn from 'classnames';
import React, { useCallback, useImperativeHandle, useMemo } from 'react';
import ListOption from './ListOption';
import ListOptionGroup from './ListOptionGroup';
import { useMessagesWithDefaults } from './messages'; // import { WidgetHTMLProps } from './shared'

import * as CustomPropTypes from './PropTypes';
import { groupBySortedKeys, makeArray, toItemArray } from './_';
import { useInstanceId } from './WidgetHelpers';
import useMutationObserver from '@restart/hooks/useMutationObserver';
import useCallbackRef from '@restart/hooks/useCallbackRef';
import useMergedRefs from '@restart/hooks/useMergedRefs';
var whitelist = ['style', 'className', 'role', 'id', 'autocomplete', 'size', 'tabIndex', 'maxLength', 'name'];
var whitelistRegex = [/^aria-/, /^data-/, /^on[A-Z]\w+/];

function pickElementProps(props) {
  var result = {};
  Object.keys(props).forEach(function (key) {
    if (whitelist.indexOf(key) !== -1 || whitelistRegex.some(function (r) {
      return !!key.match(r);
    })) result[key] = props[key];
  });
  return result;
}

export var useScrollFocusedIntoView = function useScrollFocusedIntoView(element) {
  var observeChanges = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var scrollIntoView = useCallback(function () {
    if (!element) return;
    var selectedItem = element.querySelector('[data-rw-focused]');

    if (selectedItem && selectedItem.scrollIntoView) {
      selectedItem.scrollIntoView({
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [element]);
  useMutationObserver(observeChanges ? element : null, {
    subtree: true,
    attributes: true,
    attributeFilter: ['data-rw-focused']
  }, scrollIntoView);
  return scrollIntoView;
};
export function useHandleSelect(multiple, dataItems, onChange) {
  return function (dataItem, event) {
    if (multiple === false) {
      onChange(dataItem, {
        dataItem: dataItem,
        lastValue: dataItems[0],
        originalEvent: event
      });
      return;
    }

    var checked = dataItems.includes(dataItem);
    onChange(checked ? dataItems.filter(function (d) {
      return d !== dataItem;
    }) : [].concat(_toConsumableArray(dataItems), [dataItem]), {
      dataItem: dataItem,
      lastValue: dataItems,
      action: checked ? 'remove' : 'insert',
      originalEvent: event
    });
  };
}
var List = /*#__PURE__*/React.forwardRef(function List(_ref, outerRef) {
  var _elementProps$role;

  var _ref$multiple = _ref.multiple,
      multiple = _ref$multiple === void 0 ? false : _ref$multiple,
      _ref$data = _ref.data,
      data = _ref$data === void 0 ? [] : _ref$data,
      value = _ref.value,
      onChange = _ref.onChange,
      accessors = _ref.accessors,
      className = _ref.className,
      messages = _ref.messages,
      disabled = _ref.disabled,
      renderItem = _ref.renderItem,
      renderGroup = _ref.renderGroup,
      searchTerm = _ref.searchTerm,
      groupBy = _ref.groupBy,
      elementRef = _ref.elementRef,
      _ref$optionComponent = _ref.optionComponent,
      Option = _ref$optionComponent === void 0 ? ListOption : _ref$optionComponent,
      renderList = _ref.renderList,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  var id = useInstanceId();
  var dataItems = makeArray(value, multiple);
  var groupedData = useMemo(function () {
    return groupBy ? groupBySortedKeys(groupBy, data) : undefined;
  }, [data, groupBy]);

  var _useCallbackRef = useCallbackRef(),
      _useCallbackRef2 = _slicedToArray(_useCallbackRef, 2),
      element = _useCallbackRef2[0],
      ref = _useCallbackRef2[1];

  var disabledItems = toItemArray(disabled);

  var _useMessagesWithDefau = useMessagesWithDefaults(messages),
      emptyList = _useMessagesWithDefau.emptyList;

  var divRef = useMergedRefs(ref, elementRef);
  var handleSelect = useHandleSelect(multiple, dataItems, onChange);
  var scrollIntoView = useScrollFocusedIntoView(element, true);
  var elementProps = pickElementProps(props);
  useImperativeHandle(outerRef, function () {
    return {
      scrollIntoView: scrollIntoView
    };
  }, [scrollIntoView]);

  function renderOption(item, idx) {
    var textValue = accessors.text(item);
    var itemIsDisabled = disabledItems.includes(item);
    var itemIsSelected = dataItems.includes(item);
    return /*#__PURE__*/React.createElement(Option, {
      dataItem: item,
      key: "item_".concat(idx),
      searchTerm: searchTerm,
      onSelect: handleSelect,
      disabled: itemIsDisabled,
      selected: itemIsSelected
    }, renderItem ? renderItem({
      item: item,
      searchTerm: searchTerm,
      index: idx,
      text: textValue,
      // TODO: probably remove
      value: accessors.value(item),
      disabled: itemIsDisabled
    }) : textValue);
  }

  var items = groupedData ? groupedData.map(function (_ref2, idx) {
    var _ref3 = _slicedToArray(_ref2, 2),
        group = _ref3[0],
        items = _ref3[1];

    return /*#__PURE__*/React.createElement("div", {
      role: "group",
      key: "group_".concat(idx)
    }, /*#__PURE__*/React.createElement(ListOptionGroup, null, renderGroup ? renderGroup({
      group: group
    }) : group), items.map(renderOption));
  }) : data.map(renderOption);
  var rootProps = Object.assign({
    id: id,
    tabIndex: 0,
    ref: divRef
  }, elementProps, {
    'aria-multiselectable': !!multiple,
    className: cn(className, 'rw-list'),
    role: (_elementProps$role = elementProps.role) != null ? _elementProps$role : 'listbox',
    children: React.Children.count(items) ? items : /*#__PURE__*/React.createElement("div", {
      className: "rw-list-empty"
    }, emptyList())
  });
  return renderList ? renderList(rootProps) : /*#__PURE__*/React.createElement("div", rootProps);
});
List.displayName = 'List';
export default List;